<template>
  <b-container class="edit-questionnaire">
    <div class="float-right">
      <b-button variant="default" @click="cancel()">Cancel</b-button>
      <b-btn variant="info" @click.prevent="save">
        <template v-if="!saveLoading">Save</template>
        <template v-else>
          <b-spinner small />
          <span>Saving...</span>
        </template>
      </b-btn>
    </div>

    <h2>Edit Questionnaire</h2>

    <v-wait :for="loaderId">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">Loading the data...</div>
      </template>
      <template>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Questionnaire Title:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
            >
              <b-form-input
                type="text"
                placeholder="Title"
                v-model="questionnaire.title"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <template v-if="isNonSociety">
          <b-row class="m-3">
            <b-col>
              <b-form-group
                horizontal
                label="Membership Types:"
                label-class="font-weight-bold pt-0"
                label-cols="4"
                :invalid-feedback="membershipTypesInvalidFeedback"
                :state="validateState('nonSocietyMembershipTypes')"
              >
                <b-form-select
                  v-model="$v.questionnaire.nonSocietyMembershipTypes.$model"
                  multiple
                  :options="membershipTypesOptions"
                  :state="validateState('nonSocietyMembershipTypes')"
                  :select-size="
                    membershipTypesOptions.length < 7
                      ? membershipTypesOptions.length
                      : 7
                  "
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <b-row class="m-3">
            <b-col>
              <b-form-group
                horizontal
                label="Society:"
                label-class="font-weight-bold pt-0"
                label-cols="4"
              >
                <b-form-select
                  v-model="(questionnaire.society || {}).id"
                  :options="societyOptions"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col sm="4">
              <label class="font-weight-bold">
                <strong>Is Primary:</strong>
              </label>
            </b-col>
            <b-col sm="8" class="p-2">
              <b-form-checkbox
                v-model="questionnaire.isPrimary"
                :disabled="isSingleForSociety"
                :title="titleForIsPrimary"
              />
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col>
              <b-form-group
                horizontal
                label="Society Membership Types:"
                label-class="font-weight-bold pt-0"
                label-cols="4"
                :invalid-feedback="membershipTypesInvalidFeedback"
                :state="validateState('societyMembershipTypes')"
              >
                <b-form-select
                  v-model="$v.questionnaire.societyMembershipTypes.$model"
                  multiple
                  :options="membershipTypesOptions"
                  :state="validateState('societyMembershipTypes')"
                  :select-size="
                    membershipTypesOptions.length < 7
                      ? membershipTypesOptions.length
                      : 7
                  "
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="m-3">
            <b-col>
              <b-form-group
                horizontal
                label="Specialties:"
                label-class="font-weight-bold pt-0"
                label-cols="4"
                :invalid-feedback="specialtiesInvalidFeedback"
                :state="validateState('specialties')"
              >
                <b-form-select
                  v-model="$v.questionnaire.specialties.$model"
                  multiple
                  :options="specialtyOptions"
                  :state="validateState('specialties')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
        <!--        -->
        <b-row class="m-3">
          <b-col sm="4">
            <label class="font-weight-bold">
              <strong>Frequently Used Variables:</strong>
            </label>
          </b-col>
          <b-col
            sm="8"
            class="p-2"
            v-if="questionnaire && questionnaire.sections"
          >
            <FavoriteQuestionsSelector
              button-label="Select Frequently Used Variables"
              header-label="Select Frequently Used Variables"
              ref-id="add-favorites"
              :questionnaire="questionnaire"
              :ids="favorites"
              @selected="onFavoritesSelected"
            />
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Study Type:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="studyTypeInvalidFeedback"
              :state="validateState('study_type_id')"
            >
              <b-form-select
                v-model="$v.questionnaire.study_type_id.$model"
                :options="studyTypesOptions"
                :state="validateState('study_type_id')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Start Date:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="startDateInvalidFeedback"
              :state="validateState('start_date')"
            >
              <datepicker
                input-class="form-control bg-white"
                placeholder="Start Date"
                :format="dateFormatter"
                v-model="$v.questionnaire.start_date.$model"
                :state="validateState('start_date')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="End Date:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="endDateInvalidFeedback"
              :state="validateState('end_date')"
            >
              <datepicker
                input-class="form-control bg-white"
                placeholder="End Date"
                :format="dateFormatter"
                v-model="$v.questionnaire.end_date.$model"
                :state="validateState('end_date')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Period:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="periodInvalidFeedback"
              :state="validateState('period')"
            >
              <b-form-input
                type="number"
                min="1900"
                placeholder="Period"
                max="9999"
                v-model.number="$v.questionnaire.period.$model"
                :state="validateState('period')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Active Date:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="activeDateInvalidFeedback"
              :state="validateState('active_date')"
            >
              <datepicker
                input-class="form-control bg-white"
                placeholder="Active Date"
                :format="dateFormatter"
                v-model="$v.questionnaire.active_date.$model"
                :state="validateState('active_date')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Completion Date:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
              :invalid-feedback="completionDateInvalidFeedback"
              :state="validateState('completion_date')"
            >
              <datepicker
                input-class="form-control bg-white"
                placeholder="Completion Date"
                :format="dateFormatter"
                v-model="$v.questionnaire.completion_date.$model"
                :state="validateState('completion_date')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col>
            <b-form-group
              horizontal
              label="Worksheet Filename:"
              label-class="font-weight-bold pt-0"
              label-cols="4"
            >
              <b-form-input
                type="text"
                placeholder="Worksheet Filename"
                v-model="questionnaire.worksheet_file_name"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col sm="4" class="d-flex align-items-center">
            <label class="mb-0 font-weight-bold">Supports Report:</label>
          </b-col>
          <b-col sm="8" class="p-2">
            <b-form-checkbox
              v-model="questionnaire.is_support_report"
              value="1"
              unchecked-value="0"
            />
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col sm="4" class="d-flex align-items-center">
            <label class="mb-0 font-weight-bold">
              Supports Profile Measures:
            </label>
          </b-col>
          <b-col sm="8" class="p-2">
            <b-form-checkbox
              v-model="questionnaire.supports_profile_measures"
              value="1"
              unchecked-value="0"
            />
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col sm="4" class="d-flex align-items-center">
            <label class="mb-0 font-weight-bold">
              Supports Job Preferences:
            </label>
          </b-col>
          <b-col sm="8" class="p-2">
            <b-form-checkbox
              v-model="questionnaire.is_support_job_preference"
              value="1"
              unchecked-value="0"
            />
          </b-col>
        </b-row>
        <b-row class="m-3">
          <b-col sm="4" class="d-flex align-items-center">
            <label class="mb-0 font-weight-bold"> Questionnaire Hidden: </label>
          </b-col>
          <b-col sm="8" class="p-2">
            <b-form-checkbox
              v-model="questionnaire.is_questionnaire_hidden"
              value="1"
              unchecked-value="0"
            />
          </b-col>
        </b-row>
        <div class="m-3 d-flex justify-content-end">
          <b-button
            v-if="isDemoMode && questionnaire.id"
            variant="danger"
            size="sm"
            @click="onResetSurveys"
            :disabled="loadingReset"
            class="mr-2"
          >
            <b-spinner v-if="loadingReset" small />
            <span v-else>Reset Surveys</span>
          </b-button>

          <b-button
            variant="danger"
            size="sm"
            @click="onGenerateCSV"
            :disabled="loadingCsv"
          >
            <b-spinner v-if="loadingCsv" small />
            <span v-else>Download question csv</span>
          </b-button>
        </div>
      </template>
    </v-wait>
    <v-wait for="loading">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">Loading the data...</div>
      </template>
      <b-row>
        <b-col cols="12">
          <SectionList :questionnaire="questionnaire" />
        </b-col>
      </b-row>
    </v-wait>
    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Insights Section Title:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
        >
          <b-form-input
            type="text"
            placeholder="Insights Section Title"
            v-model="questionnaire.insights_section_title"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Insights section content:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
        >
          <b-form-textarea
            placeholder="Insights section content"
            v-model="questionnaire.insights_section_content"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Report Image:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
        >
          <b-form-input
            type="text"
            :disabled="!!parseInt(questionnaire.is_support_report)"
            placeholder="Report Image"
            v-model="questionnaire.report_image"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="m-3">
      <b-col>
        <b-form-group
          horizontal
          label="Prior Questionnaire:"
          label-class="font-weight-bold pt-0"
          label-cols="4"
        >
          <b-form-select
            v-model="questionnaire.prior_year_questionnaire_id"
            :options="priorQuestionnaireOptions"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import { HalfCircleSpinner } from 'epic-spinners';
import Api from '@/providers/Api.js';
import SectionList from '@/components/questionnaire/SectionList';
import FavoriteQuestionsSelector from '@/components/survey/favorite-questions/FavoriteQuestionsSelector';
import Questionnaire from '@/providers/Questionnaire.js';
import Datepicker from 'vuejs-datepicker';
import { validationMixin } from 'vuelidate';
import {
  numeric,
  minValue,
  maxValue,
  required,
  requiredIf,
  requiredUnless,
} from 'vuelidate/lib/validators';
import {
  afterDateValidator,
  beforeDateValidator,
} from '@/helpers/customValidators';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';
import GetStudyTypeOptionsMixin from '@/mixins/GetStudyTypeOptionsMixin';
import { csvEscape } from '@/helpers/csvHelper';
import Help from '@/providers/Help';

const dateQuestionnaireKeys = [
  'start_date',
  'end_date',
  'active_date',
  'completion_date',
];

export default {
  name: 'questionnaireEdit',
  mixins: [
    validationMixin,
    GetMembershipTypeOptionsMixin,
    GetStudyTypeOptionsMixin,
  ],
  components: {
    SectionList,
    HalfCircleSpinner,
    Datepicker,
    FavoriteQuestionsSelector,
  },
  data: function () {
    return {
      saveLoading: false,
      loadingCsv: false,
      loadingReset: false,
      questionnaire: {
        start_date: null,
        end_date: null,
        period: null,
        active_date: null,
        completion_date: null,
        is_support_report: 1,
        supports_profile_measures: 1,
        is_support_job_preference: 0,
        insights_section_title: null,
        insights_section_content: null,
        report_image: null,
        societyMembershipTypes: [],
        is_questionnaire_hidden: 0,
        nonSocietyMembershipTypes: [],
      },
      favorites: Questionnaire.getDefaultVariableSelections(), // default if none stored with questionnaire
      priorQuestionnaireOptions : []
    };
  },
  validations: {
    questionnaire: {
      start_date: {
        beforeDate: beforeDateValidator('end_date'),
      },
      end_date: {
        afterDate: afterDateValidator('start_date'),
      },
      period: {
        numeric,
        minValue: minValue(1900),
        maxValue: maxValue(9999),
      },
      active_date: {
        beforeDate: beforeDateValidator('completion_date'),
      },
      completion_date: {
        afterDate: afterDateValidator('active_date'),
      },
      societyMembershipTypes: {
        required: requiredUnless(function () {
          return this.isNonSociety;
        }),
      },
      specialties: {
        required,
      },
      nonSocietyMembershipTypes: {
        required: requiredIf(function () {
          return this.isNonSociety;
        }),
      },
      study_type_id: {
        required,
      },
    },
  },
  created: function () {
    this.$wait.start(this.loaderId);

    Api.getQuestionnaireWithQuestionsAndVariables(
      this,
      this.$route.params.questionnaireId
    )
      .then((q) => {
        this.syncQuestionnaireWithData(q);
      })
      .finally(() => {
        this.$wait.end(this.loaderId);
      });
  },
  computed: {
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    societyOptions: function () {
      return this.$store.state.societyOptions;
    },
  
    isSingleForSociety: function () {
      return this.questionnaire.society
        ? parseInt(this.questionnaire.society.questionnairesCount) <= 1
        : false;
    },
    titleForIsPrimary: function () {
      return this.isSingleForSociety
        ? "Questionnaire is single per society, you can't change this"
        : '';
    },
    startDateInvalidFeedback: function () {
      if (!this.$v.questionnaire.start_date.afterDate) {
        return 'Start Date should be less than End Date.';
      }
      return null;
    },
    endDateInvalidFeedback: function () {
      if (!this.$v.questionnaire.end_date.beforeDate) {
        return 'End Date should be greater than Start Date.';
      }
      return null;
    },
    periodInvalidFeedback: function () {
      if (!this.$v.questionnaire.period.minValue) {
        return 'Period should be greater than 1900.';
      }
      if (!this.$v.questionnaire.period.maxValue) {
        return 'Period should be less than 9999.';
      }
      if (!this.$v.questionnaire.period.numeric) {
        return 'Period should be numeric.';
      }
      return null;
    },
    activeDateInvalidFeedback: function () {
      if (!this.$v.questionnaire.active_date.beforeDate) {
        return 'Active Date should be less than Completion Date.';
      }
      return null;
    },
    completionDateInvalidFeedback: function () {
      if (!this.$v.questionnaire.completion_date.afterDate) {
        return 'Completion Date should be greater than Active Date.';
      }
      return null;
    },
    membershipTypesInvalidFeedback: function () {
      return 'This field is required';
    },
    studyTypeInvalidFeedback: function () {
      return 'This field is required';
    },
    specialtiesInvalidFeedback: function () {
      return 'This field is required';
    },
    membershipTypesOptions: function () {
      const membershipTypes = this.$store.state.membershipTypes.items || [];
      return membershipTypes.map((m) => {
        return { value: m.id, text: m.type_name };
      });
    },
    studyTypesOptions: function () {
      const studyTypes = this.$store.state.studyTypes.items || [];
      return studyTypes.map((st) => {
        return { value: st.id, text: st.name };
      });
    },
    specialtyOptions: function () {
      const specialties = this.questionnaire.society.specialties || [];
      return specialties.map((sp) => {
        return { value: sp.id, text: sp.title };
      });
    },
    filteredQuestions: function () {
      return this.questionnaire.sections.reduce((data, s) => {
        s.questionSections.forEach((qS) => {
          data.push({
            ...qS.question,
            section_id: s.id,
            section_name: s.title,
          });
        });
        return data;
      }, []);
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode;
    },
    isNonSociety: function () {
      return !this.questionnaire.society;
    },
  },
  mounted() {
    this.getQuestionnaireOptionData();
  },
  watch: {
    'questionnaire.society.id': 'getQuestionnaireOptionData'
  },
  methods: {
    getQuestionnaireOptionData: async function () {
      if(this.questionnaire && this.questionnaire.society && this.questionnaire.society.id){
        let data = await Api.getQuestionnaireOptions(this.questionnaire.society.id);
        data = data.filter(item => item.id != this.questionnaire.id)
        this.priorQuestionnaireOptions = [
        { value: null, text: "Select questionnaire..." }, // Initial option
        ...data.map(option => ({ value: option.id, text: option.title }))
      ];
        //const filteredArray = qData.filter(item => item.value !== this.questionnaire.id);

      }
    },
    cancel: function () {
      this.$router.go(-1);
    },
    save: function () {
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      this.saveLoading = true;

      this.questionnaire.favorites = JSON.stringify(this.favorites);

      this.convertDateFormats();

      Api.updateQuestionnaire(this.questionnaire)
        .then((questionnaire) => {
          this.syncQuestionnaireWithData(questionnaire);
          this.$toasted.success('Questionnaire saved', { duration: 3000 });
        })
        .catch((response) => {
          this.showErrors(response);
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v.questionnaire[name];
      return $dirty ? !$error : null;
    },
    showErrors: function (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    convertDateFormats: function () {
      dateQuestionnaireKeys.forEach((key) => {
        this.questionnaire[key] = this.convertDateFormat(key);
      });
    },
    convertDateFormat: function (key) {
      return this.$dayjs(this.questionnaire[key]).format('YYYY-MM-DD');
    },
    onFavoritesSelected: function (favoritesData) {
      this.favorites = favoritesData;
    },
    syncQuestionnaireWithData: function (questionnaire) {
      if (questionnaire.society) {
        const societyQuestionnairesCount = parseInt(
          questionnaire.society.questionnairesCount
        );
        if (societyQuestionnairesCount <= 1 && !questionnaire.isPrimary) {
          //there is an error with such questionnaire set as primary
          questionnaire = { ...questionnaire, isPrimary: true };
        }
      }
      dateQuestionnaireKeys.forEach((key) => {
        if (!questionnaire[key]) return;
        questionnaire[key] = this.$dayjs
          .utc(questionnaire[key])
          .set('hour', 11)
          .set('minute', 59)
          .set('second', 59)
          .toDate();
      });
      this.questionnaire = questionnaire;
      this.questionnaire.societyMembershipTypes =
        questionnaire.societyMembershipTypes.map(({ id }) => id);
      this.questionnaire.nonSocietyMembershipTypes =
        questionnaire.nonSocietyMembershipTypes.map(({ id }) => id);
      this.questionnaire.specialties =
        questionnaire.specialties.map(({ id }) => id);
      if (questionnaire.favorites) {
        this.favorites = JSON.parse(questionnaire.favorites);
      }
    },
    dateFormatter: function (date) {
      return this.$dayjs(date).format('MMM D, YYYY');
    },
    onGenerateCSV: function () {
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent +=
        'id,preamble,prompt,explanation,label,type,required,chart,job_question_id,is_unknown_enabled,' +
        'unknown_option_label,is_select_all_enabled,condition,section_id,section_name\n';
      csvContent += this.filteredQuestions
        .map((q) =>
          [
            q.id,
            `"${csvEscape(q.preamble)}"`,
            `"${csvEscape(q.prompt)}"`,
            `"${csvEscape(q.explanation)}"`,
            `"${csvEscape(q.label)}"`,
            `"${q.type}"`,
            `"${q.required}"`,
            `"${csvEscape(JSON.stringify(q.chart))}"`,
            `"${q.job_question_id}"`,
            `"${q.is_unknown_enabled}"`,
            `"${q.unknown_option_label}"`,
            `"${q.is_select_all_enabled}"`,
            `"${
              q.condition ? csvEscape(JSON.stringify(q.condition)) : q.condition
            }"`,
            `"${q.section_id}"`,
            `"${csvEscape(q.section_name)}"`,
          ].join(',')
        )
        .join('\n');
      let encodedUri = encodeURI(csvContent);
      let link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute(
        'download',
        `Questionnaire_Questions_${this.questionnaire.id}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onResetSurveys: function () {
      Help.confirm.show(
        'Are you sure you want to reset all surveys related to this questionnaire?',
        'Accept',
        () => {
          this.resetSurveys();
        }
      );
    },
    resetSurveys: async function () {
      try {
        this.loadingReset = true;
        await Api.gmAdminResetStudies(this.questionnaire.id);
        this.$toasted.success('Studies reset successful', { duration: 3000 });
      } catch (e) {
        console.log(e.message);
      } finally {
        this.loadingReset = false;
      }
    }
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss" />
